import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, AlertTriangle, CheckCircle2, CircleDot } from "lucide-react";
import { API } from "../../context/AuthContext";
import { StatusBadge } from "./StatusBadge";
import ReviewBar from "./ReviewBar";
import SourceViewer from "./SourceViewer";
import { FieldsTab, EventsTab, LedgerTab, AuditTab } from "./DetailTabs";
import { DeadlinesTab, UndatedList } from "./DeadlinesTab";

const TABS = [
  { key: "fields", label: "Fields" },
  { key: "events", label: "Dates & Events" },
  { key: "ledger", label: "Ledger" },
  { key: "deadlines", label: "Deadlines" },
  { key: "undated", label: "Undated" },
  { key: "audit", label: "Audit" },
];

function Steps({ steps }) {
  if (!steps?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="pipeline-steps">
      {steps.map((s, i) => (
        <span key={i} title={s.detail} className={`flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${s.status === "failed" ? "text-rose-300 border-rose-500/40" : s.status === "needs_review" ? "text-amber-300 border-amber-500/30" : "text-emerald-300 border-emerald-500/25"}`}>
          {s.status === "failed" ? <AlertTriangle className="w-2.5 h-2.5" /> : s.status === "needs_review" ? <CircleDot className="w-2.5 h-2.5" /> : <CheckCircle2 className="w-2.5 h-2.5" />}
          {s.step.replace(/_/g, " ")}
        </span>
      ))}
    </div>
  );
}

export default function DocumentDetail({ documentId, caseId, canEdit, onClose, onChanged, onOpenDoc }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("fields");
  const [source, setSource] = useState(null);

  const load = useCallback(() => axios.get(`${API}/documents/${documentId}/analysis`).then((r) => setData(r.data)).catch(() => setData({ error: true })), [documentId]);
  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => {
    if (!["processing", "pending"].includes(data?.document?.status)) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [data?.document?.status, load]);

  const changed = () => { load(); onChanged(); };
  const doc = data?.document;
  const counts = { events: data?.events?.length, ledger: data?.ledger?.length, deadlines: data?.deadlines?.length, undated: data?.undated?.length, audit: data?.audit?.length, fields: data?.run?.fields?.length };
  const openSource = (page, quote) => setSource({ page, quote });

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="document-detail">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0f1729] border border-white/10 rounded-2xl w-full max-w-5xl max-h-[94vh] flex flex-col shadow-2xl">
        {!data ? <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 text-[#f59e0b] animate-spin" /></div>
          : data.error ? <div className="p-10 text-sm text-rose-300">Couldn't load this document. <button onClick={onClose} className="underline">Close</button></div> : (
            <>
              <div className="px-5 pt-4 pb-3 border-b border-white/10 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display font-bold text-white text-base truncate max-w-full" data-testid="detail-doc-name">{doc.original_name}</h3>
                      <StatusBadge status={doc.status} testId="detail-doc-status" />
                      {data.lineage.length > 1 && data.lineage.map((l) => (
                        <button key={l.document_id} onClick={() => onOpenDoc(l.document_id)} data-testid={`lineage-v${l.version}`} className={`text-[9px] font-mono px-1.5 rounded border ${l.document_id === doc.document_id ? "text-gray-950 bg-sky-300 border-sky-300" : "text-sky-300 border-sky-500/30"}`}>v{l.version}</button>
                      ))}
                    </div>
                    <div className="text-[11px] font-mono text-gray-400" data-testid="detail-classification">
                      <span className="text-[#2dd4bf]">{doc.template_display || "Not yet classified"}</span>
                      {doc.template_version && ` · template v${doc.template_version}`}
                      {doc.classification && ` · ${Math.round(doc.classification.confidence * 100)}% confidence`}
                      {doc.duplicate_of && " · identical file already in this case"}
                    </div>
                    {doc.classification?.basis && <div className="text-[11px] text-gray-500 italic">{doc.classification.basis}</div>}
                  </div>
                  <button onClick={onClose} data-testid="detail-close-btn" className="p-1.5 text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <Steps steps={doc.pipeline_steps} />
                {doc.error && <div className="text-xs text-rose-300 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="detail-error">{doc.error}</div>}
                {doc.review?.reason && <div className="text-[11px] text-gray-400">Review note ({doc.review.by}): “{doc.review.reason}”</div>}
                {!!doc.discrepancies?.length && (
                  <div className="border border-rose-500/30 rounded-lg px-3 py-2 space-y-0.5" data-testid="detail-discrepancies">
                    {doc.discrepancies.map((d, i) => <div key={i} className="text-[11px] text-rose-200 flex gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{d.description}</div>)}
                  </div>
                )}
                {!!doc.validation?.warnings?.length && <div className="text-[10px] font-mono text-amber-300">{doc.validation.warnings.join(" · ")}</div>}
                <ReviewBar doc={doc} caseId={caseId} canEdit={canEdit && ["owner", "editor"].includes(data.my_role)} onDone={changed} onReplaced={() => { onChanged(); onClose(); }} />
              </div>
              <div className="px-5 pt-3 flex gap-1 overflow-x-auto shrink-0 border-b border-white/5">
                {TABS.map((t) => (
                  <button key={t.key} onClick={() => setTab(t.key)} data-testid={`detail-tab-${t.key}`}
                    className={`text-[11px] font-mono font-bold px-3 py-2 border-b-2 whitespace-nowrap transition-colors duration-200 ${tab === t.key ? "border-[#f59e0b] text-white" : "border-transparent text-gray-500 hover:text-gray-300"}`}>
                    {t.label}{counts[t.key] ? ` (${counts[t.key]})` : ""}
                  </button>
                ))}
              </div>
              <div className="p-5 overflow-y-auto flex-1">
                {tab === "fields" && (data.run ? <FieldsTab fields={data.run.fields} onSource={openSource} /> : <div className="text-xs italic text-gray-500">No completed extraction yet.</div>)}
                {tab === "events" && <EventsTab events={data.events} canEdit={canEdit} onSource={openSource} onChanged={changed} />}
                {tab === "ledger" && <LedgerTab ledger={data.ledger} reconciliation={data.run?.reconciliation} onSource={openSource} />}
                {tab === "deadlines" && <DeadlinesTab deadlines={data.deadlines} canEdit={canEdit} onSource={openSource} onChanged={changed} />}
                {tab === "undated" && <UndatedList facts={data.undated.filter((f) => ["needs_review", "reviewed"].includes(f.review_status))} canEdit={canEdit} onSource={openSource} onChanged={changed} />}
                {tab === "audit" && <AuditTab audit={data.audit} runs={data.runs} />}
              </div>
            </>
          )}
      </div>
      {source && <SourceViewer documentId={documentId} page={source.page} quote={source.quote} onClose={() => setSource(null)} />}
    </div>
  );
}
